import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { combineLatest, filter, Observable, tap } from 'rxjs';
import { LetModule, PushModule } from '@ngrx/component';
import { NgIf, NgOptimizedImage } from '@angular/common';
import { WINDOW } from '@ng-web-apis/common';
import { ImageFile, ImageMedia } from '@nx-icilalune/drupal-types';
import { isScullyRunning } from '@scullyio/ng-lib';

export type ObjectFit = 'cover' | 'contain';

export const profiles = [64, 256, 512, 640, 1024, 2048] as const;
export type Profiles = typeof profiles[number];

@Component({
  selector: 'i2l-media-image',
  standalone: true,
  template: `
    <ng-container *ngrxLet="imageMedia$; let imageMedia">
      <ng-container *ngrxLet="imageFile$; let imageFile">
        <ng-container *ngrxLet="isInViewport$; let isInViewport">
          <ng-container *ngIf="imageMedia && imageFile">
            <img
              #imageElement
              [src]="imageFile.webpUrl"
              [alt]="imageMedia.alt"
              [width]="imageFile.width"
              [height]="imageFile.height"
              [attr.loading]="isInViewport ? 'eager' : 'lazy'"
              (load)="onImageLoaded()"
            />
          </ng-container>
        </ng-container>
      </ng-container>
    </ng-container>
    <ng-content></ng-content>
  `,
  styleUrls: ['./media-image.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PushModule, LetModule, NgIf, NgOptimizedImage],
})
export class MediaImageComponent
  extends ComponentStore<{
    profile?: Profiles;
    imageMedia?: ImageMedia;
    imageFile?: ImageFile;
    isInViewport?: boolean;
    loading: boolean;
    loaded: boolean;
  }>
  implements OnInit, OnDestroy
{
  vm$ = this.select((s) => s);
  profiles$ = this.select((s) => s.profile);
  imageMedia$ = this.select((s) => s.imageMedia);
  imageFile$ = this.select((s) => s.imageFile);
  loaded$ = this.select((s) => s.loaded);
  isInViewport$ = this.select((s) => s.isInViewport);

  setImageSrc = this.effect(
    (combined$: Observable<[Profiles | undefined, ImageMedia | undefined]>) => {
      return combined$.pipe(
        filter(([profile, image]) => {
          return !!profile && !!image;
        }),
        tap(([profile, image]) => {
          if (image) {
            const { nativeElement } = this.el;
            const isVertical =
              nativeElement.offsetHeight > nativeElement.offsetWidth;
            let imageFile: ImageFile | undefined;
            const devicePixelRatio = this.window.devicePixelRatio >= 2 ? 2 : 1;
            if (isVertical) {
              imageFile = image?.images?.find(
                (i) =>
                  i.profileId.indexOf('crop') === -1 &&
                  i.height >= nativeElement.offsetHeight * devicePixelRatio
              );
            } else {
              imageFile = image?.images?.find(
                (i) =>
                  i.profileId.indexOf('crop') === -1 &&
                  i.width >= nativeElement.offsetWidth * devicePixelRatio
              );
            }
            if (imageFile) {
              this.patchState(() => {
                return {
                  imageFile,
                };
              });
            }
          }
        })
      );
    }
  );

  @Input()
  set profile(value: Profiles) {
    this.setState((state) => {
      return {
        ...state,
        profile: value,
      };
    });
  }

  @Input()
  set image(value: ImageMedia | undefined) {
    this.setState((state) => {
      return {
        ...state,
        imageMedia: value,
      };
    });
  }

  #objectFit: ObjectFit = 'cover';
  @Input()
  set objectFit(value: ObjectFit) {
    this.#validateObjectFit(value, this.#objectFit);
    this.#objectFit = value;
  }

  @ViewChild('imageElement') imageElement!: ElementRef<HTMLImageElement>;

  #resizeObserver!: ResizeObserver;

  constructor(
    private renderer: Renderer2,
    private el: ElementRef<HTMLElement>,
    @Inject(PLATFORM_ID) platformId: string,
    @Inject(WINDOW) private window: Window
  ) {
    super({ loading: true, loaded: false });
    if (isScullyRunning()) {
      this.addLoadedClass();
    }
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }
  }

  #validateObjectFit(
    newValue: ObjectFit,
    oldValue: ObjectFit | undefined = undefined
  ) {
    const { nativeElement } = this.el;
    if (oldValue) {
      this.renderer.removeClass(nativeElement, `object-fit-${oldValue}`);
    }
    this.renderer.addClass(nativeElement, `object-fit-${newValue}`);
  }

  setSrc() {
    const { nativeElement } = this.el;
    const elementWidth = nativeElement.offsetWidth;
    const largerIndex = profiles.findIndex((p) => p > elementWidth);
    let selectedProfile: Profiles;
    if (largerIndex > -1) {
      selectedProfile = profiles[largerIndex];
    } else {
      selectedProfile = profiles[profiles.length - 1];
    }
    this.setState((state) => {
      return {
        ...state,
        profile: selectedProfile,
        isInViewport: this.isInViewport(),
      };
    });
    this.setImageSrc(combineLatest([this.profiles$, this.imageMedia$]));
  }

  ngOnInit(): void {
    this.#resizeObserver = new ResizeObserver((entries) => {
      this.setSrc();
    });
    this.#resizeObserver.observe(this.el.nativeElement);
  }

  ngAfterViewInit(): void {
    this.#validateObjectFit(this.#objectFit);
  }

  onImageLoaded() {
    this.addLoadedClass();
  }

  isInViewport() {
    const { nativeElement } = this.el;
    const rect = nativeElement.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= this.window.innerHeight &&
      rect.right <= this.window.innerWidth
    );
  }

  addLoadedClass() {
    const { nativeElement } = this.el;
    nativeElement.classList.add('loaded');
  }
}
